<script>
import { apiSecond } from '@/boot/axios'

import { aquaApiPrefix } from './aquaForms'
import AquaSelect from './AquaSelect.vue'

const SUBJECT_FORMS = [1, 2, 4, 5, 6]
const FEED_FORMS = [3]
const REQUIRED_FORMS = [1, 2, 3, 6]
const DEADLINE_DAY = 10
const SUBMITTED = ['subject_signed', 'tir_signed']

export default {
	name: 'AquaSubjectForms',
	components: { AquaSelect },
	props: {
		year: {
			type: Number,
			required: true,
		},
		isFeedProducer: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			loading: true,
			regionId: null,
			regionTitles: {},
			titlesRequested: false,
			reports: {},
		}
	},
	computed: {
		forms() {
			return this.isFeedProducer ? FEED_FORMS : SUBJECT_FORMS
		},
		regions() {
			return (this.$store.state.userObject?.regions || []).map((item) => ({
				id: item.region_id,
				title: this.regionTitles[item.region_id] || item.region_id,
			}))
		},
		isDeadlinePassed() {
			return new Date() > new Date(this.year + 1, 0, DEADLINE_DAY, 23, 59, 59)
		},
		rows() {
			return this.forms.map((form) => {
				const report = this.reports[form] || null
				const required = REQUIRED_FORMS.includes(form)
				return {
					form,
					report,
					required,
					status: report?.status?.value || null,
					overdue: required && this.isDeadlinePassed && !report?.submitted_at,
				}
			})
		},
		requiredRows() {
			return this.rows.filter((row) => row.required)
		},
		submittedRequired() {
			return this.requiredRows.filter((row) => SUBMITTED.includes(row.status)).length
		},
	},
	watch: {
		year() {
			this.loadReports()
		},
		regionId() {
			this.loadReports()
		},
		regions: {
			immediate: true,
			handler(regions) {
				if (!this.regionId && regions.length) this.regionId = regions[0].id
				if (regions.length > 1 && !this.titlesRequested) this.loadRegionTitles()
			},
		},
	},
	mounted() {
		this.loadReports()
	},
	methods: {
		async loadRegionTitles() {
			this.titlesRequested = true
			const response = await apiSecond
				.get('/v2/handbook-regions', { params: { page: 1, per_page: 100 } })
				.catch(() => null)
			this.regionTitles = Object.fromEntries((response?.data?.data || []).map((region) => [region.id, region.title]))
		},
		async loadReports() {
			if (!this.regionId) return
			this.loading = true
			const params = { report_year: this.year, region_id: this.regionId, per_page: 1 }
			const results = await Promise.all(
				this.forms.map((form) =>
					apiSecond
						.get(aquaApiPrefix('subject', form), { params })
						.then(({ data }) => [form, (data.data || [])[0] || null])
						.catch(() => [form, null]),
				),
			)
			this.reports = Object.fromEntries(results)
			this.loading = false
		},
		isLateSubmitted(row) {
			return Boolean(row.report?.is_late && row.report.submitted_at)
		},
		statusDate(row) {
			const report = row.report
			const dates = report && {
				draft: report.created_at,
				subject_signed: report.submitted_at,
				tir_signed: report.accepted_at,
				canceled_by_tir: report.returned_at,
			}
			return ((dates && dates[row.status]) || '—').split(' ')[0]
		},
		statusText(row) {
			if (!row.status) return this.$t('aqua.reporting.not_formed')
			if (row.status === 'canceled_by_tir') return this.$t('aqua.reporting.on_revision')
			return this.$t(`aqua.status.${row.status}`)
		},
		action(row) {
			const base = `/${this.$i18n.locale}/account/reporting/aqua/${row.form}`
			if (!row.report) {
				return {
					text: this.$t('aqua.reporting.fill'),
					to: { path: `${base}/create`, query: { report_year: this.year, region_id: this.regionId } },
					kind: 'primary',
				}
			}
			if (row.status === 'draft') return { text: this.$t('aqua.reporting.continue'), to: `${base}/${row.report.id}/edit`, kind: 'outline' }
			if (row.status === 'canceled_by_tir') {
				return { text: this.$t('aqua.reporting.edit'), to: `${base}/${row.report.id}/edit`, kind: 'primary' }
			}
			return { text: this.$t('aqua.reporting.view'), to: `${base}/${row.report.id}`, kind: 'view' }
		},
		listLink(form) {
			return `/${this.$i18n.locale}/account/reporting/aqua/${form}`
		},
	},
}
</script>

<template>
	<div class="aqua-forms">
		<div class="aqua-toolbar">
			<AquaSelect
				v-if="regions.length > 1"
				v-model="regionId"
				:label="$t('inputs.region')"
				:options="regions.map((region) => ({ value: region.id, text: region.title }))"
			/>
			<span v-if="loading" class="aqua-progress aqua-skeleton aqua-skeleton--progress" aria-hidden="true"></span>
			<span
				v-else
				class="aqua-progress"
				:class="submittedRequired === requiredRows.length ? 'aqua-progress--ok' : 'aqua-progress--wait'"
			>
				{{ $t('aqua.reporting.progress', { done: submittedRequired, total: requiredRows.length }) }}
			</span>
		</div>

		<p v-if="!regions.length" class="aqua-empty">{{ $t('aqua.reporting.no_region') }}</p>

		<div v-else class="aqua-rows" :aria-busy="String(loading)">
			<div v-for="row in rows" :key="row.form" class="aqua-row" :class="{ 'aqua-row--overdue': !loading && row.overdue }">
				<span class="aqua-row__doc">
					<svg width="20" height="22" viewBox="0 0 20 22" fill="none" stroke="currentColor" stroke-width="1.8">
						<path d="M3 1h10l5 5v15H3z" />
						<path d="M7 10h8M7 14h8M7 18h5" />
					</svg>
				</span>
				<span class="aqua-row__code">{{ row.form }}-аква</span>
				<span class="aqua-row__name">
					{{ $t(`aqua.reporting.forms_short.${row.form}`) }}
					<router-link :to="listLink(row.form)" class="aqua-row__history">{{ $t('aqua.reporting.all_reports') }}</router-link>
				</span>
				<span class="aqua-badge" :class="row.required ? 'aqua-badge--required' : 'aqua-badge--optional'">
					{{ row.required ? $t('aqua.reporting.required') : $t('aqua.reporting.optional') }}
				</span>
				<span v-if="loading" class="aqua-row__status" aria-hidden="true">
					<span class="aqua-skeleton aqua-skeleton--icon"></span>
					<span>
						<span class="aqua-skeleton aqua-skeleton--line"></span>
						<span class="aqua-skeleton aqua-skeleton--line aqua-skeleton--short"></span>
					</span>
				</span>
				<span v-else class="aqua-row__status" :class="`aqua-status--${row.status || 'none'}`">
					<svg v-if="row.status === 'tir_signed'" class="aqua-status__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<circle cx="12" cy="12" r="10" />
						<path d="M7.5 12.5l3 3 6-6.5" />
					</svg>
					<svg v-else-if="row.status === 'canceled_by_tir' || row.status === 'subject_signed'" class="aqua-status__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
						<circle cx="12" cy="12" r="10" />
						<path d="M12 6.5V12l3.5 2" />
					</svg>
					<svg v-else-if="row.status === 'draft'" class="aqua-status__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
						<path d="M4 20l1-4.5L15.5 5a2 2 0 012.8 0l.7.7a2 2 0 010 2.8L8.5 19 4 20z" />
						<path d="M13.5 7l3.5 3.5" />
					</svg>
					<svg v-else class="aqua-status__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
						<circle cx="12" cy="12" r="10" />
					</svg>
					<span>
						<strong>{{ statusText(row) }}</strong>
						<small>{{ statusDate(row) }}</small>
						<small v-if="row.overdue" class="aqua-row__overdue">{{ $t('aqua.reporting.overdue') }}</small>
						<small v-else-if="isLateSubmitted(row)" class="aqua-row__late">{{ $t('aqua.reporting.late') }}</small>
					</span>
				</span>
				<span v-if="loading" class="aqua-skeleton aqua-skeleton--action" aria-hidden="true"></span>
				<router-link v-else :to="action(row).to" class="aqua-row__action" :class="`is-${action(row).kind}`">
					<svg v-if="action(row).kind === 'view'" width="22" height="16" viewBox="0 0 22 16" fill="none" stroke="currentColor" stroke-width="1.8">
						<path d="M1 8s3.6-7 10-7 10 7 10 7-3.6 7-10 7S1 8 1 8z" />
						<circle cx="11" cy="8" r="3" />
					</svg>
					{{ action(row).text }}
				</router-link>
				<div v-if="!loading && row.status === 'canceled_by_tir' && row.report.return_comment" class="aqua-row__comment">
					{{ $t('aqua.reporting.tir_comment') }}: {{ row.report.return_comment }}
				</div>
			</div>
		</div>
	</div>
</template>

<style scoped>
.aqua-toolbar {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	justify-content: flex-end;
	gap: 16px;
	margin-bottom: 12px;
}
.aqua-toolbar .aqua-select {
	width: 280px;
	margin-right: auto;
}
.aqua-progress {
	padding: 6px 12px;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
}
.aqua-progress--ok {
	background: #e3f8ec;
	color: #0f8a4f;
}
.aqua-progress--wait {
	background: #fff4dc;
	color: #b26a00;
}
.aqua-empty {
	color: #475f84;
}
.aqua-skeleton {
	display: block;
	border-radius: 6px;
	background: linear-gradient(90deg, #edf2f9 25%, #f7faff 50%, #edf2f9 75%);
	background-size: 200% 100%;
	animation: aqua-shimmer 1.2s ease-in-out infinite;
}
.aqua-skeleton--progress {
	width: 150px;
	height: 32px;
}
.aqua-skeleton--icon {
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	border-radius: 50%;
}
.aqua-skeleton--line {
	width: 120px;
	height: 12px;
	margin: 3px 0;
}
.aqua-skeleton--short {
	width: 72px;
}
.aqua-skeleton--action {
	justify-self: end;
	width: 160px;
	height: 44px;
	border-radius: 10px;
}
@keyframes aqua-shimmer {
	from {
		background-position: 100% 0;
	}
	to {
		background-position: -100% 0;
	}
}
@media (prefers-reduced-motion: reduce) {
	.aqua-skeleton {
		animation: none;
	}
}
.aqua-row {
	display: grid;
	grid-template-columns: 48px 90px minmax(180px, 1fr) 150px 210px 170px;
	align-items: center;
	gap: 12px;
	padding: 12px 16px;
	margin-bottom: 8px;
	border: 1px solid #edf2f9;
	border-radius: 12px;
	background: #fff;
	transition: box-shadow 0.2s;
}
.aqua-row:hover {
	box-shadow: 0 4px 14px rgba(21, 127, 238, 0.08);
}
.aqua-row--overdue {
	border-left: 3px solid #f64a4a;
}
.aqua-row__doc {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	border-radius: 10px;
	background: #eaf3fd;
	color: #157fee;
}
.aqua-row__code {
	font-weight: 700;
	text-transform: uppercase;
	color: #151d48;
}
.aqua-row__name {
	display: flex;
	flex-direction: column;
	color: #151d48;
}
.aqua-row__history {
	align-self: flex-start;
	margin-top: 2px;
	font-size: 13px;
	color: #157fee;
}
.aqua-row__history:hover,
.aqua-row__history:focus-visible {
	text-decoration: underline;
	outline: none;
}
.aqua-badge {
	justify-self: start;
	padding: 4px 12px;
	border-radius: 8px;
	font-size: 13px;
}
.aqua-badge--required {
	background: #fde8e7;
	color: #d93025;
}
.aqua-badge--optional {
	background: #eaf3fd;
	color: #157fee;
}
.aqua-row__status {
	display: flex;
	align-items: center;
	gap: 10px;
	color: #9aa7b8;
}
.aqua-row__status strong {
	display: block;
	font-weight: 600;
	color: #151d48;
}
.aqua-row__status small {
	display: block;
	color: #6b7a90;
}
.aqua-status__icon {
	width: 24px;
	height: 24px;
	flex-shrink: 0;
}
.aqua-status--tir_signed {
	color: #0f8a4f;
}
.aqua-status--tir_signed strong {
	color: #0f8a4f;
}
.aqua-status--canceled_by_tir {
	color: #e8710a;
}
.aqua-status--canceled_by_tir strong {
	color: #e8710a;
}
.aqua-status--subject_signed {
	color: #157fee;
}
.aqua-status--subject_signed strong {
	color: #157fee;
}
.aqua-status--draft {
	color: #475f84;
}
.aqua-row__overdue {
	color: #d93025 !important;
	font-weight: 600;
}
.aqua-row__late {
	color: #b26a00 !important;
}
.aqua-row__action {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 8px;
	justify-self: end;
	min-width: 160px;
	padding: 10px 18px;
	border: 1px solid #c9d6e6;
	border-radius: 10px;
	color: #151d48;
	font-weight: 500;
	transition: background 0.2s, border-color 0.2s;
}
.aqua-row__action:hover {
	border-color: #157fee;
}
.aqua-row__action.is-primary {
	border-color: #157fee;
	background: #157fee;
	color: #fff;
}
.aqua-row__action.is-primary:hover {
	background: #0f6fd6;
}
.aqua-row__comment {
	grid-column: 3 / -1;
	padding: 8px 12px;
	border-radius: 8px;
	background: #fff4dc;
	color: #7a4a00;
	font-size: 13px;
}
@media (max-width: 1280px) {
	.aqua-row {
		grid-template-columns: 44px 80px 1fr;
	}
	.aqua-badge,
	.aqua-row__status {
		grid-column: 3;
	}
	.aqua-row__action {
		grid-column: 1 / -1;
		justify-self: stretch;
	}
	.aqua-row__comment {
		grid-column: 1 / -1;
	}
}
</style>
